import dayjs from 'dayjs'
import React from 'react'
import { useParams } from 'react-router-dom'

import { ConcealedText, Dashboard, PageLayout, SideSummary } from '@components/common'
import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import {
  Avatar,
  BackButton,
  Button,
  ButtonClean,
  ConfirmModal,
  Dropdown,
  HeadContent,
  Heading,
  Loading,
  Subsection,
  Tag,
  ToolbarButton,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import { DotsHorizontalIcon } from '@fourthwall/icons'
import { isWebView } from '@fourthwall/utils'
import { getCountryNameByCode } from '@fourthwall/utils/dist/inputs'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'
import { reverse, routing } from '@utils/routing'

import { ChatCreateParams } from '../Messages/types'
import { BadgePreview } from '../Settings/modules/Badges/components/BadgePreview'
import { getEmojiIcon } from '../Settings/modules/Badges/utils'
import { MemberSkeleton } from './MemberSkeleton'
import { SurveyHeading } from './components/SurveyHeading/SurveyHeading'
import { SurveyList } from './components/SurveyList/SurveyList'
import { REASONS_MAP } from './consts'
import { useMemberExitSurveysQuery } from './hooks/useMemberExitSurveysQuery'
import { useMemberQuery } from './hooks/useMemberQuery'
import { useRemoveMemberShadowBanMutation } from './hooks/useRemoveMemberShadowBanMutation'
import { useShadowBanMemberMutation } from './hooks/useShadowBanMemberMutation'
import { RefundModal } from './modules/RefundModal'
import { SubscriptionCancel } from './modules/SubscriptionCancel'
import * as S from './styled'

export const Member = () => {
  const modal = useModalContext()
  const media = useMediaQueryContext()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const { memberId } = useParams<'memberId'>()
  const { memberQuery } = useMemberQuery([Number(memberId)])
  const { memberExitSurveysQuery } = useMemberExitSurveysQuery([{ member_id: Number(memberId) }])
  const { shadowBanMemberMutation } = useShadowBanMemberMutation()
  const { removeMemberShadowBanMutation } = useRemoveMemberShadowBanMutation()

  if (!memberQuery.isSuccess || !memberExitSurveysQuery.isSuccess) {
    return <MemberSkeleton />
  }

  const member = memberQuery.data
  const exitSurveys = memberExitSurveysQuery.data.exitSurveys

  const getName = () => {
    return (
      <S.NameContainer>
        <S.AvatarContainer>
          <Avatar name={member.nickname} imageUrl={member.avatarUrl} />
        </S.AvatarContainer>
        <Heading type="h5" weight="bold">
          {member.nickname}
        </Heading>
      </S.NameContainer>
    )
  }

  const getSubscriptionTierInfo = () => {
    const billingCycle = member.tier?.billingCycle === 'Monthly' ? 'Paid monthly' : 'Paid annually'
    const tier = `${member.tier?.name} tier ${(member.state.type === 'Active' || member.state.type === 'Expiring') && member.state.freeTrialEndsAt ? ' (Free trial)' : ''}`

    return (
      <>
        <p>{tier}</p>
        <p>{billingCycle}</p>
      </>
    )
  }

  const getSubscriptionInfo = () => {
    if (member.state.type === 'Active') {
      const monthsCount = dayjs(new Date()).diff(dayjs(member.state.subscribedAt), 'months')
      const tagLabel = monthsCount ? `${monthsCount}-month member` : 'New member'

      return (
        <>
          <S.TagContainer>
            <Tag appearance="success" label={tagLabel} />
          </S.TagContainer>
          {getSubscriptionTierInfo()}
          <p>
            Subscribed{' '}
            <TimezonedDateString
              date={member.state.subscribedAt}
              format="MMM D, YYYY, h:mm A"
              withTimezoneAbbreviation={false}
            />
          </p>
        </>
      )
    }

    if (member.state.type === 'Expiring') {
      return (
        <>
          <S.TagContainer>
            <Tag appearance="alert" label="Expiring member" />
          </S.TagContainer>
          {getSubscriptionTierInfo()}
          <p>
            Subscribed <TimezonedDateString date={member.state.subscribedAt} format="MMM D, YYYY" />
          </p>
          <p>
            Expires <TimezonedDateString date={member.state.expiresAt} format="MMM D, YYYY" />
          </p>
        </>
      )
    }

    if (member.state.type === 'PastDue') {
      return (
        <>
          <S.TagContainer>
            <Tag appearance="alert" label="Past due member" />
          </S.TagContainer>
          {getSubscriptionTierInfo()}
          <p>
            Subscribed <TimezonedDateString date={member.state.subscribedAt} format="MMM D, YYYY" />
          </p>
          <p>
            Payment failed{' '}
            <TimezonedDateString date={member.state.previousPeriodEnd} format="MMM D, YYYY" />
          </p>
        </>
      )
    }

    if (member.state.type === 'Former') {
      return (
        <>
          <S.TagContainer>
            <Tag appearance="default" label="Former member" />
          </S.TagContainer>
          {getSubscriptionTierInfo()}
          <p>
            Subscribed <TimezonedDateString date={member.state.subscribedAt} format="MMM D, YYYY" />
          </p>
          <p>
            Expired <TimezonedDateString date={member.state.expiredAt} format="MMM D, YYYY" />
          </p>
        </>
      )
    }

    if (member.state.type === 'None') {
      return <p>No subscription</p>
    }
  }

  const renderPerksByMail = () => {
    if (member.wantsToReceiveBenefits === true) {
      return (
        <SideSummary.Item title="Perks by mail">
          <p>Opted in</p>
        </SideSummary.Item>
      )
    }

    if (member.wantsToReceiveBenefits === false) {
      return (
        <SideSummary.Item title="Perks by mail">
          <p>Member opted out from receiving perks by mail</p>
        </SideSummary.Item>
      )
    }

    if (member.tier?.askForShippingAddress === true) {
      return (
        <SideSummary.Item title="Perks by mail">
          <p>No shipping address provided</p>
        </SideSummary.Item>
      )
    }

    return null
  }

  const renderShippingAddress = () => {
    if (!member.shippingAddress && member.tier?.askForShippingAddress) {
      return (
        <SideSummary.Item title="Shipping address">
          <p>No shipping address provided</p>
        </SideSummary.Item>
      )
    }

    if (!member.shippingAddress) return null

    const { name, address1, address2, city, state, zip, country, phone, vatId } =
      member.shippingAddress

    const vatIdPrefix: Record<string, string> = {
      BR: 'CPF number',
      MX: 'Tax ID (CURL or RFC)',
    }

    return (
      <SideSummary.Item title="Shipping address">
        <ConcealedText showLabel="View shipping address" hideLabel="Hide shipping address">
          <p>{name}</p>
          <p>{address1}</p>
          {address2 && <p>{address2}</p>}
          <p>{`${city}${state ? `, ${state}` : ''}${zip ? `, ${zip}` : ''}`}</p>
          <p>{getCountryNameByCode(country)}</p>
          {phone && <p>{phone}</p>}
          {vatId && <p>{`${vatIdPrefix[country]}: ${vatId}`}</p>}
        </ConcealedText>
      </SideSummary.Item>
    )
  }

  const renderTitle = () => {
    return (
      <S.TitleContainer>
        {!media.tablet ? renderSendMessageButton() : null}
        {member.shadowBanned && (
          <div>
            <span style={{ fontWeight: 600 }}>Shadowbanned</span> (
            <ButtonClean withUnderline label="remove ban" onClick={removeShadowBan} />)
          </div>
        )}
      </S.TitleContainer>
    )
  }

  const getMemberBadge = () => {
    return (
      <S.Badges>
        {member.badges?.map((badge) => (
          <BadgePreview
            key={badge.id}
            emoji={getEmojiIcon(badge.icon)}
            text={badge.text}
            backgroundColor={badge.backgroundColor}
            borderColor={badge.borderColor}
            textColor={badge.textColor}
            iconOnly={badge.minimal}
          />
        ))}
      </S.Badges>
    )
  }

  const getAccountOrigin = () => {
    if (member.accountOrigin === 'FreeAccount') {
      return 'Created through "Create free account" signup flow'
    }

    if (member.accountOrigin === 'Checkout' && member.state.type === 'None') {
      return 'Started a paid memberships checkout but did not complete checkout'
    }

    return 'Created through checkout flow'
  }

  const shadowBanMember = () => {
    modal.open(ConfirmModal, {
      title: `Are you sure you want to shadowban ${member.nickname} (${member.email})?`,
      text: 'Shadowbanned members will not be aware they have been shadowbanned and can still access the members-only area, but comments they leave will only be visible to them and not be visible to you or any other members.',
      confirmLabel: 'Shadowban member',
      onConfirm: () => {
        shadowBanMemberMutation.mutateAsync([{ member_id: member.id }], {
          onSuccess: () => {
            memberQuery.refetch()
          },
        })
      },
    })
  }

  const removeShadowBan = () => {
    modal.open(ConfirmModal, {
      title: `Are you sure you want to remove shadowban from ${member.nickname} (${member.email})?`,
      text: 'All comments of this member will become visible to everyone again.',
      confirmLabel: 'Remove shadowban',
      onConfirm: () => {
        removeMemberShadowBanMutation.mutateAsync([{ member_id: member.id }], {
          onSuccess: () => {
            memberQuery.refetch()
          },
        })
      },
    })
  }

  const issueRefund = () => {
    modal.open(RefundModal, {
      member: memberQuery.data,
      onSuccess: memberQuery.refetch,
    })
  }

  const renderSendMessageButton = () => {
    if (!dashboardFeatureFlags.membershipsMessages.enabled) return null

    if (member.conversationId) {
      return (
        <Button
          label="Send message"
          appearance="primary"
          size="small"
          to={reverse(routing.memberships.messages.message.self, {
            messageId: member.conversationId,
          })}
        />
      )
    }

    return (
      <Button
        label="Send message"
        appearance="primary"
        size="small"
        to={`${routing.memberships.messages.create}?${ChatCreateParams.memberId}=${member.id}`}
      />
    )
  }

  const renderToolbar = () => {
    return (
      <>
        <Dropdown
          content={
            <>
              {member.shadowBanned ? (
                <Dropdown.Item label="Remove shadowban" onClick={removeShadowBan} />
              ) : (
                <Dropdown.Item label="Shadowban" onClick={shadowBanMember} />
              )}
              <SubscriptionCancel member={member} onSuccess={memberQuery.refetch} />
              {member && member.state.type !== 'None' && (
                <Dropdown.Item label="Issue a refund" onClick={issueRefund} />
              )}
            </>
          }
          position="bottom-end"
        >
          <ToolbarButton
            label="Settings"
            icon={DotsHorizontalIcon}
            size={media.tablet ? 'medium' : 'small'}
          />
        </Dropdown>
        {media.tablet && renderSendMessageButton()}
      </>
    )
  }

  const renderExitSurveys = () => {
    if (exitSurveys.length === 0) return null

    return (
      <Subsection title="Exit survey">
        <SurveyList>
          {exitSurveys.map((survey) => (
            <div key={survey.id}>
              <SurveyHeading>
                Survey filled on{' '}
                <TimezonedDateString
                  date={survey.updatedAt}
                  format="h:mm A, MMM D, YYYY"
                  withTimezoneAbbreviation={false}
                />
              </SurveyHeading>
              <ul style={{ listStyle: 'disc', paddingLeft: 20 }}>
                {survey.reasons.map((reason) => (
                  <li key={reason}>{REASONS_MAP[reason] || `Other: ${reason}`}</li>
                ))}
              </ul>
            </div>
          ))}
        </SurveyList>
      </Subsection>
    )
  }

  return (
    <Loading
      isLoading={
        shadowBanMemberMutation.isLoading ||
        removeMemberShadowBanMutation.isLoading ||
        memberQuery.isFetching
      }
    >
      <HeadContent title={member.nickname} />
      <PageLayout
        leftContent={
          <>
            <Subsection title="Account creation">
              <ul style={{ listStyle: 'disc', paddingLeft: 20 }}>
                <li>
                  Joined{' '}
                  <TimezonedDateString
                    date={member.joinedAt}
                    format="h:mm A, MMM D, YYYY"
                    withTimezoneAbbreviation={false}
                  />
                </li>
                <li>{getAccountOrigin()}</li>
              </ul>
            </Subsection>
            {renderExitSurveys()}
          </>
        }
        rightContent={
          <SideSummary>
            <SideSummary.Item title="Supporter details">
              <p>{member.nickname}</p>
              <p>{member.email}</p>
            </SideSummary.Item>
            <SideSummary.Item title="Membership">{getSubscriptionInfo()}</SideSummary.Item>
            {renderShippingAddress()}
            {renderPerksByMail()}
          </SideSummary>
        }
      >
        <Dashboard.Section
          name={getName()}
          topContent={
            isWebView() ? undefined : (
              <BackButton
                label="Members"
                to={{
                  pathname: routing.memberships.members.self,
                  search: location.search,
                }}
              />
            )
          }
          titleSpacing="medium"
          bottomMargin="large"
          title={renderTitle()}
          toolbarContent={renderToolbar()}
          bottomContent={getMemberBadge()}
        />
      </PageLayout>
    </Loading>
  )
}
